import type { DefaultBillableResult } from '@/modules/time'
import { usePermissions } from '@/hooks/usePermissions'
import { timeEntryApi } from '@/modules/time'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery } from '@tanstack/react-query'

import { ModuleTypeEnum, PermissionActionEnum } from '@scrambl/shared'

import { queryKeys } from '../query-keys'

/**
 * The billable value a new entry linked to `moduleType`/`moduleId` gets when
 * nobody picks one: time category > project > client > internal rule >
 * organization default. No module means time without a client, which the
 * internal rule decides.
 *
 * Older APIs don't have the endpoint; callers treat a missing result as "use
 * the organization default" and still omit `billable` so the API decides.
 */
export function useDefaultBillableQuery(
  moduleType: string | null | undefined,
  moduleId: string | null | undefined,
  options?: { enabled?: boolean; categoryId?: string | null },
) {
  const categoryId = options?.categoryId || null
  const organizationId = useOrganizationStore(
    state => state.currentOrganization?.id,
  )
  const isExternalUser = useOrganizationStore(
    state => state.currentOrganization?.isExternalAccess === true,
  )
  const { checkPermission } = usePermissions()
  const canRead =
    !isExternalUser ||
    checkPermission(ModuleTypeEnum.TIME_ENTRY, PermissionActionEnum.READ)
  const hasModule = !!moduleType && !!moduleId

  return useQuery<DefaultBillableResult>({
    queryKey: queryKeys.timeEntries.defaultBillable(
      organizationId,
      moduleType,
      moduleId,
      categoryId,
    ),
    queryFn: () =>
      timeEntryApi.getDefaultBillable(
        hasModule ? moduleType : null,
        hasModule ? moduleId : null,
        categoryId,
      ),
    enabled: options?.enabled !== false && !!organizationId && canRead,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

/** Muted hint shown under a billable switch that follows the default. */
export function describeDefaultBillable(
  result: DefaultBillableResult | null | undefined,
  clientLabel = 'client',
): string | null {
  if (!result) return null
  const state = result.billable ? 'Billable' : 'Non-billable'
  switch (result.source) {
    case 'CATEGORY':
      return `${state} by default for this category`
    case 'PROJECT':
      return `${state} by default for this project`
    case 'CLIENT':
      return `${state} by default for this ${clientLabel}`
    case 'INTERNAL':
      return result.billable
        ? 'Internal time is billable by default'
        : 'Internal time is non-billable by default'
    case 'ORGANIZATION':
      return `${state} by default for your organization`
    default:
      return null
  }
}
